import React, {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useReducer,
    useState,
} from 'react';

import { type DossierOpenSearch, useFormaliteService } from '@europrocurement/l2d-domain';
import { type DataProviderProps, type DataState } from '@europrocurement/l2d-modules/providers';
import { type CollectionOpenSearchCombinedJsonld } from '@europrocurement/l2d-domain/openApi/ApiFormalite';

export type CustomCollectionOpenSearch = {
    dossiers: Array<DossierOpenSearch>;
} & Record<string, unknown> &
    Omit<CollectionOpenSearchCombinedJsonld, 'dossiers'>;

type CurrentCollectionProviderState = DataState & {
    data: CustomCollectionOpenSearch | undefined;
    currentIdCollection: number | null | undefined;
};
type CurrentCollectionProviderProps = DataProviderProps & {
    idCollection: string;
};

const INIT_CURRENT_COLLECTION_CTX = 'init_current_collection_ctx';
const REFRESH_CURRENT_COLLECTION_CTX = 'refresh_current_collection_ctx';
const UPDATE_CURRENT_COLLECTION_CTX = 'update_current_collection_ctx';

type Action =
    | { type: 'init_current_collection_ctx'; payload: CustomCollectionOpenSearch }
    | { type: 'refresh_current_collection_ctx' }
    | {
          type: 'update_current_collection_ctx';
          payload: CustomCollectionOpenSearch;
      };

const CurrentCollectionContext = createContext<CurrentCollectionProviderState | null>(null);
const CurrentCollectionDispatchContext = createContext<React.Dispatch<Action> | null>(null);

function CurrentCollectionReducer(
    state: CurrentCollectionProviderState,
    action: Action,
): CurrentCollectionProviderState {
    switch (action.type) {
        case INIT_CURRENT_COLLECTION_CTX: {
            return {
                ...state,
                loaded: true,
                currentIdCollection: action.payload.id,
                data: action.payload,
            };
        }

        // Update currentDossier and siblings if exist in context
        case UPDATE_CURRENT_COLLECTION_CTX: {
            return {
                ...state,
                data: action.payload,
            };
        }

        case REFRESH_CURRENT_COLLECTION_CTX: {
            return {
                ...state,
                loaded: false,
            };
        }
        default: {
            // @ts-expect-error property type never
            throw new Error(`Unknown action: ${action.type}`);
        }
    }
}

const CurrentCollectionProvider: React.FunctionComponent<
    PropsWithChildren<CurrentCollectionProviderProps>
> = function ({ autoload, children, idCollection }) {
    const [state, dispatch] = useReducer(CurrentCollectionReducer, {
        loaded: false,
        data: undefined,
        currentIdCollection: undefined,
    } as CurrentCollectionProviderState);

    const { collectionOpenSearchApi } = useFormaliteService();
    const [isFetching, setIsFetching] = useState<boolean>(false);

    // Fetch current dossier by props params and dispatch in context
    const fetchAndInitCollection = async (collectionId: string) => {
        setIsFetching(true);

        try {
            const res = await collectionOpenSearchApi.apiOpensearchCollectionsIdGet({
                id: collectionId,
            });

            dispatch({
                type: INIT_CURRENT_COLLECTION_CTX,
                payload: res.data as unknown as CustomCollectionOpenSearch,
            });
        } catch (error) {
            console.error(error);
        } finally {
            setIsFetching(false);
        }
    };

    // Fetch current dossier by props params
    useEffect(() => {
        if (!autoload || state.loaded === true || isFetching === true) {
            return;
        }
        fetchAndInitCollection(idCollection);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collectionOpenSearchApi, autoload, state.loaded]);

    // Use Effect to refresh data when the idCollection props change (from url)
    useEffect(() => {
        if (
            state.currentIdCollection &&
            state.currentIdCollection !== parseInt(idCollection, 10) &&
            autoload &&
            state.loaded === true &&
            !isFetching
        ) {
            dispatch({
                type: REFRESH_CURRENT_COLLECTION_CTX,
            });
        }
    }, [autoload, idCollection, isFetching, state.currentIdCollection, state.loaded]);

    return (
        <CurrentCollectionContext.Provider value={state}>
            <CurrentCollectionDispatchContext.Provider value={dispatch}>
                {children}
            </CurrentCollectionDispatchContext.Provider>
        </CurrentCollectionContext.Provider>
    );
};

const useCurrentCollectionCtx = () => {
    const state = useContext(CurrentCollectionContext);

    const isDataLoaded = (): boolean => state?.loaded || false;

    const getCurrentCollection = (): CustomCollectionOpenSearch | undefined => state?.data;

    return {
        dossiers: state?.data?.dossiers,
        collection: state?.data,
        isDataLoaded,
        getCurrentCollection,
    };
};

const useCurrentCollectionCtxDispatch = () => useContext(CurrentCollectionDispatchContext);

// Méthode get
export {
    CurrentCollectionProvider,
    CurrentCollectionContext,
    CurrentCollectionDispatchContext,
    // hooks
    useCurrentCollectionCtx,
    useCurrentCollectionCtxDispatch,
    // actions dispatch
    INIT_CURRENT_COLLECTION_CTX,
    REFRESH_CURRENT_COLLECTION_CTX,
    UPDATE_CURRENT_COLLECTION_CTX,
};
